import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import AppContext from 'Utils/AppContext';
import { filterPayloadForExport } from 'Utils/entityLevels';
import OverviewPanel from './OverviewPanel/OverviewPanel';
import EntityPanel from './EntityPanel/EntityPanel';
import TablePanel from './TablePanel/TablePanel';
import RunMetadataBar from './OverviewPanel/RunMetadataBar';
import DeleteModal from './DeleteModal';
import CancelJobModal from './CancelJobModal';
import { Switch, Route, useRouteMatch, useParams, useHistory } from "react-router-dom";
import { commafy, EngineIcon, getDataLabel, getDisplayDate, getDisplayTime } from 'Utils';
import exportFromJSON from 'export-from-json';
import { getMeta, getJob, getResultsPayloadForExport, deleteRun, cancelDiscoverJob, clearDiscoverResultsCache } from 'DataDiscovery/discoverApi';
import { buildCsvRows } from './exportCsvRows';
import CandidateSourcesPage from '../ScanMeta/CandidateSourcesPage';
import StructuredColumnPage from './TablePanel/StructuredColumnPage';
import { dataSourceDetailsForExport, getDataSourceNumericId } from 'DataSources/datasourcesApi';
import { BsDownload } from 'react-icons/bs';
import { Dropdown } from 'react-bootstrap';
import { usePolling } from 'Hooks';
import FetchContainer from 'Utils/FetchContainer';
import SearchProgress from './SearchProgress';
import PageHeader from 'Utils/PageHeader';
import { MdCancel } from 'react-icons/md';
import AdoptedFromBadge from 'DataSources/AdoptedFromBadge';

const ReportPanel = (props) => {
  const history = useHistory();
  const params = useParams();
  const { path } = useRouteMatch();
  const { entityLevels, isReadOnly } = useContext(AppContext);
  // const [tableSampleCount, setTableSampleCount] = useState({});
  const [isEncrypted, setIsEncrypted] = useState(false);
  const [encryptedTables, setEncryptedTables] = useState({});
  const searchid = params.search;
  let searchName = searchid;
  const [deleteSearchId, setDeleteSearchId] = useState(searchid)
  const [dbname, setDbname] = useState('');
  const [schemaName, setSchemaName] = useState('');
  const [schemaSearchid, setSchemaSearchid] = useState('');
  const [engine, setEngine] = useState('');
  const [connection, setConnection] = useState('');
  const [connectionId, setConnectionId] = useState(null);
  const [totalTableCount, setTotalTableCount] = useState(0);
  const [totalSensitiveTableCount, setTotalSensitiveTableCount] = useState(0);
  // Relational tables the worker skipped pre-flight because they were empty (0 rows). These
  // are not counted in totalTableCount, so report them separately. 0 for file/NoSQL runs.
  const [emptySkippedCount, setEmptySkippedCount] = useState(0);
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayId, setDisplayId] = useState(searchid);
  const [searchDate, setSearchDate] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCancelJobModal, setShowCancelJobModal] = useState(false);
  const [error, setError] = useState(null);
  const [exportingFindings, setExportingFindings] = useState(false);
  const [polling, setPolling] = useState(null);
  const [sampleRules, setSampleRules] = useState('');
  const [runStats, setRunStats] = useState(null);
  // Resolve the run's data source so RunMetadataBar (A1a) and the file-aware
  // dataLabel (A2 label fix) can both use datasourceType — the reliable signal
  // for "is this a file/JSON/XML source," independent of the unverified engine
  // string. Mirrors OverviewPanel's connectionDetails resolution, simplified
  // since connectionId here is already reliably sourced from meta.dataSourceId.
  const connectionDetails = useMemo(
    () => (connectionId != null ? (props.connections || []).find((c) => c?.id === connectionId) : null),
    [props.connections, connectionId]
  );
  const dataLabel = getDataLabel(engine, connectionDetails?.datasourceType);
  const searchesInProgress = props.searchesInProgress || {};
  const inProgress = searchesInProgress[searchid];

  const loadSearchData = async (searchid, opts = {}) => {
    try {
      setError(null);

      const meta = await getMeta(searchid, opts);

      setDeleteSearchId(searchid);

      const connectionName = typeof meta.connection === 'string' ? meta.connection : (meta.connection?.name ?? '');
      const databaseValue = meta.database ?? (meta.connection?.connectionDetails?.database ?? '');
      const schemaValue =
        meta.connectionDetails?.schema ?? meta.connection?.connectionDetails?.schema ?? databaseValue;

      setTotalTableCount(meta.table_count ?? (meta.tables?.length ?? 0));
      setTotalSensitiveTableCount(meta.sensitive_table_count ?? 0);
      setEmptySkippedCount(meta.empty_tables_skipped_count ?? 0);
      setTables(meta.tables ?? []);
      setEngine(meta.engine ?? '');
      setConnection(connectionName);
      setConnectionId(meta.dataSourceId ?? null);
      setDbname(databaseValue);
      setSchemaName(schemaValue || databaseValue);
      setSchemaSearchid(searchid);
      // Title: the operator-entered run label when there is one; otherwise
      // "<data source> — <run date>" rather than the bare job id.
      const runDateMs = meta.completed_at ?? meta.start_date;
      const unlabeledTitle = connectionName
        ? `${connectionName}${runDateMs ? ` — ${getDisplayDate(runDateMs)} ${getDisplayTime(runDateMs)}` : ''}`
        : meta.label || searchid;
      setDisplayId(meta.runLabel || unlabeledTitle);
      setSearchDate(meta.completed_at != null ? meta.completed_at : '');
      setSampleRules(meta.sampling_rules ?? '');
      setRunStats({
        status: meta.status ?? null,
        start_date: meta.start_date ?? null,
        completed_at: meta.completed_at ?? null,
        workItemKind: meta.workItemKind ?? null,
        workItemsCompleted: meta.workItemsCompleted ?? null,
        workItemsTotal: meta.workItemsTotal ?? null,
        error_message: meta.error_message ?? null,
      });

      setIsEncrypted(false);
      setEncryptedTables({});

      setLoading(false);
    } catch (e) {
      console.log(e);
      setError(e?.message ?? 'Failed to load report');
    }
  };

  useEffect(() => {
    const init = async (searchid) => {
      setLoading(true);
      await loadSearchData(searchid);
      setLoading(false);
    }
    if (searchid) init(searchid);
  }, [searchid]);

  useEffect(() => {
    const pollingValue = inProgress ? 15000 : null;
    setPolling(pollingValue);
  }, [inProgress]);

  // While RUNNING this is a cheap status-only poll — getMeta no longer pulls the full results
  // payload until the run is terminal (audit R6). loadSearchData does one more fetch on the
  // completion transition below to populate the header table/sensitive counts from the final
  // payload, since the last in-progress poll saw an empty payload.
  usePolling(async () => loadSearchData(searchid), polling);

  // Slow partial-results poll: every 5 minutes while the run is in progress, pull the
  // platform's stored partial snapshot (a platform-DB read — no worker /results call, so
  // this cannot recreate the audit-R6 overview N+1 storm the old 10-15s polls caused).
  // Populating the header counts is what opens the render gate below, so the panels mount
  // mid-run with partial findings. partialTick tells mounted panels to refetch; they hit
  // the results cache this pull just warmed, so it stays one payload transfer per window.
  const [partialTick, setPartialTick] = useState(0);
  usePolling(async () => {
    if (!inProgress || !searchid) return;
    clearDiscoverResultsCache(searchid);
    await loadSearchData(searchid, { withResults: true });
    setPartialTick((t) => t + 1);
  }, inProgress ? 300000 : null);

  const wasInProgress = useRef(false);
  useEffect(() => {
    if (wasInProgress.current && !inProgress && searchid) {
      loadSearchData(searchid);
    }
    wasInProgress.current = !!inProgress;
  }, [inProgress, searchid]);

  const searchDeleteHandler = async () => {
    await deleteRun(searchid);
    if (props.fetchNewResults) props.fetchNewResults();
    history.push('/discover');
  };

  const cancelJobHandler = async () => {
    try {
      setLoading(true);
      await cancelDiscoverJob(searchid);
      setShowCancelJobModal(false);
      await loadSearchData(searchid);
    } catch (e) {
      console.log(e);
      setError(e?.message || 'Failed to cancel job');
    } finally {
      setLoading(false);
    }
  };

  const handleExportFindings = async (format = 'json') => {
    if (exportingFindings) return;
    setExportingFindings(true);
    setError(null);
    try {
      // getResultsPayloadForExport reassembles Folder Search runs from their per-folder
      // chunks (the stored payload only holds the folder index); classic runs return
      // the stored payload unchanged.
      const [job, exportPayload] = await Promise.all([getJob(searchid), getResultsPayloadForExport(searchid)]);
      const dsName = job?.dataSourceName ?? '';
      // Resolve by immutable id first — dataSourceName is a snapshot taken at job
      // creation and goes stale if the source is renamed afterwards.
      const matched =
        (job?.dataSourceId != null
          ? (props.connections || []).find((c) => getDataSourceNumericId(c) === Number(job.dataSourceId))
          : null) ||
        (props.connections || []).find((c) => String(c?.name || '') === String(dsName));
      const dataSource =
        dataSourceDetailsForExport(matched) ??
        (dsName || job?.engine != null || job?.dataSourceId != null
          ? {
              ...(dsName ? { name: dsName } : {}),
              ...(job?.engine != null ? { engine: job.engine } : {}),
              ...(job?.dataSourceId != null ? { dataSourceId: job.dataSourceId } : {}),
            }
          : {});

      const rawEntities = exportPayload;
      // Install-wide entity levels: exclude HIDDEN and REPORT_ONLY entities from the export
      // (REPORT_ONLY stays visible on screen but must not leave the product).
      const entities = filterPayloadForExport(rawEntities, entityLevels);

      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const rawName = (displayId || searchid || '').toString().trim();
      const safeName = rawName.replace(/[\\/:*?"<>|]+/g, '_').replace(/\s+/g, '-') || 'discovery';
      if (format === 'csv') {
        const tables = Array.isArray(entities) ? entities : (entities?.tables ?? []);
        // Prefer the value carried in the results payload; fall back to the run's meta
        // (dbname/schemaName state) and finally to the matched data source's own
        // connection details — the JDBC results payload often omits the top-level
        // database/schema, which left both columns blank on export.
        const database =
          (!Array.isArray(entities) && (entities?.database ?? entities?.dbname)) ||
          dbname ||
          dataSource?.database ||
          '';
        const schemaValue =
          schemaName ||
          (!Array.isArray(entities) && entities?.schema) ||
          dataSource?.schemaName ||
          dataSource?.schema ||
          database;
        // Every table and column is exported — columns without a finding read "No findings"
        // with count 0 — so the CSV doubles as an inventory of what was scanned.
        const rows = buildCsvRows(tables, { dataSource: dsName || connection || '', database, schema: schemaValue });
        exportFromJSON({
          data: rows,
          fileName: `${safeName}-${stamp}`,
          exportType: exportFromJSON.types.csv,
        });
      } else {
        exportFromJSON({
          data: { searchid, dataSource, entities },
          fileName: `${safeName}-${stamp}`,
          exportType: exportFromJSON.types.json,
        });
      }
    } catch (e) {
      console.log(e);
      setError(e?.message ?? 'Export failed');
    } finally {
      setExportingFindings(false);
    }
  };

  // Fill the sg-domain-icon badge (react-icons constrains the glyph to a square).
  // Size comes from PageHeader; no inner margin — mr-2 pushed the glyph off-center.
  const EngineIconRef = ({ size }) => <EngineIcon size={size} engine={engine} />

  // Look up the run's data source so we can show an "imported from <integration>" badge
  // in the page header. The run DTO doesn't carry origin — see umbrella TODO for backend
  // enrichment that would remove this client-side join.
  const runDataSource = connectionId != null
    ? (props.connections || []).find((c) => c && String(c.id) === String(connectionId))
    : null;
  const registeredServiceId = runDataSource?.registeredServiceId || null;
  const headerContent = registeredServiceId ? (
    <span className='d-inline-flex align-items-center'>
      {displayId}
      <AdoptedFromBadge
        registeredServiceId={registeredServiceId}
        registeredServiceName={runDataSource?.registeredServiceName}
      />
    </span>
  ) : displayId;

  return (
    <div className='h-100 d-flex flex-column'>
      { inProgress && (
        <div
          role="heading"
          aria-level="1"
          className='mb-3 text-left'
        >
          <SearchProgress inProgress={inProgress} displayId={displayId} />
        </div>
      )}
      <FetchContainer fetchingData={loading} error={error} absolute={true}>
        <PageHeader
          header={headerContent}
          icon={EngineIconRef}
          tooltip='Here are your discovery results.'
          actions={[
            <Dropdown.Item
              key="export"
              data-testid='discovery-report-export-json'
              disabled={exportingFindings}
              onClick={() => {
                handleExportFindings('json');
              }}
            >
              <BsDownload className="mr-2" />
              {exportingFindings ? 'Exporting…' : 'Export Findings (JSON)'}
            </Dropdown.Item>,
            <Dropdown.Item
              key="export-csv"
              data-testid='discovery-report-export-csv'
              disabled={exportingFindings}
              onClick={() => {
                handleExportFindings('csv');
              }}
            >
              <BsDownload className="mr-2" />
              {exportingFindings ? 'Exporting…' : 'Export Findings (CSV)'}
            </Dropdown.Item>,
            ...(inProgress ? [
              <Dropdown.Item key="cancel-job" disabled={!!isReadOnly} onClick={() => setShowCancelJobModal(true)}>
                <MdCancel className='mr-2' />
                Cancel Job
              </Dropdown.Item>
            ] : []),
            <Dropdown.Divider key="divider" />,
            <Dropdown.Item key="delete" data-testid='discovery-report-delete' disabled={!!isReadOnly} onClick={() => setShowDeleteModal(!showDeleteModal)}>
              Delete
            </Dropdown.Item>
          ]}
        />
        {(() => {
          if (totalSensitiveTableCount === 0) {
            return (
              <div 
                className='w-100 h-100 justify-content-center d-flex flex-column' 
                style={{minHeight: '600px'}}
              >
                <div className='text-center mb-3'>
                  <img 
                    src='/c2_grayscale_icon.svg' 
                    style={{ width: '100px', height: '100px', opacity: 0.75 }}
                  />
                </div>
                { totalTableCount === 0 || inProgress ? (
                  <div>
                    { inProgress
                      ? `Discover running for ${connection || searchName}. No sensitive data found yet.`
                      : `Missing results for ${searchName}.`
                    }
                  </div>
                ) : (
                  <div className='text-center'>
                    No sensitive data found.
                    Analyzed <b>{commafy(totalTableCount)}</b> {dataLabel}
                    { emptySkippedCount > 0
                      ? <> (skipped <b>{commafy(emptySkippedCount)}</b> empty {dataLabel})</>
                      : null }.
                  </div>
                )}

              </div>
            );
          }
          return (
            <div className='h-100 d-flex flex-column'>
              <Switch>
                {/* Must precede the :entity catch-alls or the literal segment is read
                    as an entity name. */}
                <Route path={`${path}/embedded/:table/:column`}>
                  <StructuredColumnPage />
                </Route>
                <Route path={`${path}/candidate-sources`}>
                  <CandidateSourcesPage
                    connections={props.connections}
                    connection={connection}
                    connectionId={connectionId}
                  />
                </Route>
                <Route path={`${path}/:entity/:table+`}>
                  <TablePanel
                    dataLabel={dataLabel}
                    connection={connection}
                    engine={engine}
                    datasourceType={connectionDetails?.datasourceType}
                    setError={(e) => setError(e)}
                    inProgress={inProgress}
                    partialTick={partialTick}
                    isEncrypted={isEncrypted}
                    encryptedTables={encryptedTables}
                  />
                </Route>
                <Route path={`${path}/:entity`}>
                  <EntityPanel
                    totalTableCount={totalTableCount}
                    totalSensitiveTableCount={totalSensitiveTableCount}
                    dataLabel={dataLabel}
                    setError={(e) => setError(e)}
                    inProgress={inProgress}
                    partialTick={partialTick}
                    encryptedTables={encryptedTables}
                    searchid={searchid}
                    connection={connection}
                  />
                </Route>
                <Route exact path={`${path}`}>
                  {(!inProgress && totalTableCount === 1 && tables[0]?.name) ? (
                    <>
                      <RunMetadataBar
                        connectionDetails={connectionDetails}
                        searchDate={searchDate}
                        sampleRules={sampleRules}
                        runStats={runStats}
                        engine={engine}
                        dbname={dbname}
                        inProgress={inProgress}
                      />
                      <TablePanel
                        table={tables[0].name}
                        searchid={searchid}
                        entity='ALL'
                        dataLabel={dataLabel}
                        connection={connection}
                        engine={engine}
                        datasourceType={connectionDetails?.datasourceType}
                        setError={(e) => setError(e)}
                        inProgress={inProgress}
                        isEncrypted={isEncrypted}
                        encryptedTables={encryptedTables}
                      />
                    </>
                  ) : (
                    <OverviewPanel
                      connections={props.connections}
                      sampleRules={sampleRules}
                      runStats={runStats}
                      polling={polling}
                      dataLabel={dataLabel}
                      totalTableCount={totalTableCount}
                      totalSensitiveTableCount={totalSensitiveTableCount}
                      searchid={searchid}
                      searchName={searchName}
                      currentSearch={props.currentSearch}
                      displayId={displayId}
                      searchDate={searchDate}
                      connection={connection}
                      connectionId={connectionId}
                      engine={engine}
                      schemaSearchid={schemaSearchid}
                      dbname={dbname}
                      isEncrypted={isEncrypted}
                      encryptedTables={encryptedTables}
                      setError={(e) => setError(e)}
                      inProgress={inProgress}
                      partialTick={partialTick}
                    />
                  )}
                </Route>
              </Switch>
            </div>
          );
        })()}
        <DeleteModal 
          showDeleteModal={showDeleteModal}
          setShowDeleteModal={(v) => setShowDeleteModal(v)}
          searchDeleteHandler={() => searchDeleteHandler()}
          searchid={searchid}
          fetchingData={loading}
        />
        <CancelJobModal
          show={showCancelJobModal}
          setShow={setShowCancelJobModal}
          cancelJobHandler={cancelJobHandler}
          jobId={searchid}
          fetchingData={loading}
        />
      </FetchContainer>
    </div>
  );
}

export default ReportPanel;